import { useState } from 'react';

import Button from '../../../common/components/button/button';
import PageHeader from '../../../common/components/page-header/page-header';
import RelativeTime from '../../../common/components/relative-time/relative-time';
import Section from '../../../common/components/section/section';
import StatusPill from '../../../common/components/status-pill/status-pill';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import {
    DECISION_RESULT,
    FINDING_RESULT_LABEL,
    FINDING_RESULT_TONE,
    WORKFLOW_STATE_LABEL,
} from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import { dataSource } from '../../../global/config/data-source';
import Gate from '../../../global/contexts/auth/gate';
import { nameOf, useProjectCatalog } from '../../../global/contexts/catalogs/catalogs';
import { useTask, useTaskReviewRounds } from '../../../global/contexts/work/work';
import DecisionForm from '../forms/decision';

import '../styles/detail.css';

function Verdict({ outcome }) {
    const isApproved = outcome.result === DECISION_RESULT.APPROVED;
    const state = outcome.response?.effect?.applied?.workflow_state;
    return (
        <div className={`review-detail__verdict review-detail__verdict--${isApproved ? 'approved' : 'returned'}`} role="status">
            <p className="review-detail__verdict-title">{isApproved ? 'Entrega aceptada' : 'Entrega devuelta'}</p>
            <p>
                {state ? `La tarea quedó en «${WORKFLOW_STATE_LABEL[state] ?? state}». ` : ''}
                {dataSource.isDemo ? 'Solo en la demostración: Mastermind real no cambió.' : 'Core confirmó la decisión.'}
            </p>
            <Button size="small" to={ROUTES.REVIEWS}>Volver a revisiones</Button>
        </div>
    );
}

export default function ReviewDetail({ taskId }) {
    const taskQuery = useTask(taskId);
    const roundsQuery = useTaskReviewRounds(taskId);
    const { data: projects } = useProjectCatalog();
    const [pendingResult, setPendingResult] = useState(null);
    const [outcome, setOutcome] = useState(null);

    if (taskQuery.isPending || roundsQuery.isPending) return <LoadingState label="Cargando revisión" rows={4} />;
    const error = taskQuery.error || roundsQuery.error;
    if (error) {
        return (
            <>
                <PageHeader backTo={ROUTES.REVIEWS} backLabel="Revisiones" title="Revisión" />
                <ErrorState error={error} title="No pudimos abrir esta revisión" onRetry={() => { taskQuery.refetch(); roundsQuery.refetch(); }} />
            </>
        );
    }

    const task = taskQuery.data;
    const round = roundsQuery.data.items.find((item) => item.isOpen) ?? null;

    const handleDecided = (result) => {
        setPendingResult(null);
        setOutcome(result);
    };

    return (
        <div className="review-detail">
            <PageHeader
                backTo={ROUTES.REVIEWS}
                backLabel="Revisiones"
                eyebrow={`Revisión · #${task.id} · ${nameOf(projects, task.projectId, 'Proyecto')}`}
                title={task.title}
                actions={<Button size="small" variant="ghost" to={ROUTES.TASK(task.id)}>Ver tarea completa</Button>}
            />

            {outcome && <Verdict outcome={outcome} />}

            {!round && !outcome && (
                <EmptyState
                    title="No hay una ronda abierta"
                    description="Esta tarea no espera decisión ahora mismo. Revisa su historial en el detalle."
                    action={<Button size="small" to={ROUTES.TASK(task.id)}>Abrir tarea</Button>}
                />
            )}

            {round && !outcome && (
                <>
                    <section className="review-detail__round" aria-label={`Ronda ${round.number}`}>
                        <div className="review-detail__scores">
                            <p className="review-detail__score review-detail__score--ok"><strong>{round.summary.cumple}</strong> cumplen</p>
                            <p className="review-detail__score review-detail__score--bad"><strong>{round.summary.incumple}</strong> incumplen</p>
                            <p className="review-detail__score review-detail__score--unknown"><strong>{round.summary.no_comprobado}</strong> sin comprobar</p>
                        </div>
                        <p className="review-detail__delivery">
                            Ronda {round.number} entregada por <span className="mono">{round.deliveredBy ?? '—'}</span> <RelativeTime value={round.deliveredAt} />
                            {' · '}<span className="mono">{round.baseCommit} → {round.deliveredCommit}</span>
                            {!round.versioned && <StatusPill tone="blocked">Sin versionar</StatusPill>}
                        </p>
                    </section>

                    <Section title="Hallazgos por criterio" count={round.findings.length}>
                        <ol className="review-detail__findings">
                            {round.findings.map((finding) => (
                                <li key={finding.index} className={`review-detail__finding review-detail__finding--${finding.result}`}>
                                    <div className="review-detail__finding-head">
                                        <span className="review-detail__finding-index">Criterio {finding.index + 1}</span>
                                        <StatusPill tone={FINDING_RESULT_TONE[finding.result]}>{FINDING_RESULT_LABEL[finding.result]}</StatusPill>
                                    </div>
                                    <p className="review-detail__criterion">{finding.criterion}</p>
                                    {finding.note && <p className="review-detail__note">{finding.note}</p>}
                                </li>
                            ))}
                        </ol>
                    </Section>

                    <Gate
                        domain="tasks:review"
                        fallback={<p className="review-detail__locked">Necesitas el permiso tasks:review para decidir esta ronda.</p>}
                    >
                        <div className="review-detail__actions">
                            <p className="review-detail__actions-hint">
                                {dataSource.isDemo
                                    ? 'Demostración: decidir aquí solo cambia los datos ficticios de esta sesión.'
                                    : 'La decisión se registra en Core con tu sesión.'}
                            </p>
                            <div className="review-detail__buttons">
                                <Button variant="danger" onClick={() => setPendingResult(DECISION_RESULT.RETURNED)}>Devolver…</Button>
                                <Button variant="positive" onClick={() => setPendingResult(DECISION_RESULT.APPROVED)}>Aceptar…</Button>
                            </div>
                        </div>
                    </Gate>

                    {pendingResult && (
                        <DecisionForm
                            taskId={task.id}
                            round={round}
                            result={pendingResult}
                            onClose={() => setPendingResult(null)}
                            onDecided={handleDecided}
                        />
                    )}
                </>
            )}
        </div>
    );
}
